import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { Badge } from "../ui/badge";
import { Skeleton } from "../ui/skeleton";
import { SERVICE_ICONS, CATEGORIES } from "../../data/content";

const CATEGORY_COLORS = {
  Build: "border-primary/30 bg-primary/10 text-primary",
  Sell: "border-accent/30 bg-accent/10 text-accent",
  Secure: "border-emerald-400/30 bg-emerald-400/10 text-emerald-400",
  Scale: "border-sky-400/30 bg-sky-400/10 text-sky-400",
};

const scrollToContact = () => {
  const el = document.querySelector("#contact");
  if (el) el.scrollIntoView({ behavior: "smooth" });
};

export const Services = ({ services, loading }) => {
  const [category, setCategory] = useState("All");

  const filtered =
    category === "All" ? services : services.filter((s) => s.category === category);

  return (
    <section id="services" className="scroll-mt-20 py-16 md:py-24" data-testid="services-section">
      <div className="mx-auto max-w-6xl px-4 sm:px-6">
        <motion.div
          initial={{ opacity: 0, y: 14 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.6 }}
          className="max-w-2xl"
        >
          <p className="text-sm font-medium text-primary tracking-wide uppercase">What We Do</p>
          <h2 className="font-display text-3xl md:text-4xl font-semibold tracking-tight text-foreground mt-3">
            One consultancy for everything IT
          </h2>
          <p className="text-muted-foreground mt-4 leading-relaxed">
            From your first website to a complete business system — sixteen services,
            one trusted point of contact.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 10 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="flex flex-wrap gap-2 mt-8"
        >
          {CATEGORIES.map((cat) => (
            <button
              key={cat}
              onClick={() => setCategory(cat)}
              className={`px-4 py-2 rounded-full text-sm font-medium border transition-colors ${
                category === cat
                  ? "bg-primary text-primary-foreground border-primary"
                  : "bg-white/5 text-muted-foreground border-white/10 hover:bg-white/10 hover:text-foreground"
              }`}
              data-testid={`services-filter-${cat.toLowerCase()}`}
            >
              {cat}
            </button>
          ))}
        </motion.div>

        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6 mt-10">
            {Array.from({ length: 6 }).map((_, i) => (
              <Skeleton key={i} className="h-56 rounded-2xl bg-white/5" />
            ))}
          </div>
        ) : (
          <motion.div layout className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6 mt-10">
            <AnimatePresence mode="popLayout">
              {filtered.map((service, i) => {
                const Icon = SERVICE_ICONS[service.id] || ArrowUpRight;
                return (
                  <motion.div
                    key={service.id}
                    layout
                    initial={{ opacity: 0, y: 18 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.96 }}
                    viewport={{ once: true, amount: 0.15 }}
                    transition={{ duration: 0.45, delay: (i % 6) * 0.05 }}
                    className={`group glass-card glass-card-hover p-5 md:p-6 flex flex-col ${
                      service.featured ? "lg:col-span-1 ring-1 ring-primary/20" : ""
                    }`}
                    data-testid={`services-card-${service.id}`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="h-11 w-11 rounded-xl bg-primary/10 border border-primary/25 flex items-center justify-center transition-transform duration-300 group-hover:rotate-6">
                        <Icon className="h-5 w-5 text-primary" />
                      </div>
                      <Badge
                        variant="outline"
                        className={`text-xs rounded-full ${CATEGORY_COLORS[service.category] || "border-white/15 text-muted-foreground"}`}
                      >
                        {service.category}
                      </Badge>
                    </div>
                    <h3 className="font-display text-lg font-semibold text-foreground mt-4">
                      {service.title}
                    </h3>
                    <p className="text-sm text-muted-foreground mt-2 leading-relaxed flex-1">
                      {service.description}
                    </p>
                    <div className="flex flex-wrap gap-1.5 mt-4">
                      {(service.features || []).map((f) => (
                        <span
                          key={f}
                          className="text-xs px-2.5 py-1 rounded-full bg-white/5 border border-white/10 text-muted-foreground"
                        >
                          {f}
                        </span>
                      ))}
                    </div>
                    <button
                      onClick={scrollToContact}
                      className="mt-5 inline-flex items-center gap-1 text-sm font-medium text-primary hover:gap-2 transition-all"
                      data-testid={`services-discuss-${service.id}`}
                    >
                      Discuss <ArrowUpRight className="h-4 w-4" />
                    </button>
                  </motion.div>
                );
              })}
            </AnimatePresence>
          </motion.div>
        )}
      </div>
    </section>
  );
};
